import type { TProject } from "@/zapi-frontend/stores/project";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";
import {
  EMPTY_VALUE_TOKEN,
  HAS_VALUE_TOKEN,
  MODIFIED_BY_FILTER_KEY,
  OWNER_FILTER_KEY,
  STATUS_FILTER_KEY
} from "../const/const";
import type { TActiveFilters } from "./portfolioStorage";

export type TPortfolioChatScope = {
  projectIds: number[];
  draftIds: number[];
  description: string;
};

const DRAFT_ID_PREFIX = "draft-";

function filterLabel(key: string, dimensions: TOrganizationProjectDimension[]): string {
  if (key === STATUS_FILTER_KEY) return "Status";
  if (key === OWNER_FILTER_KEY) return "Owner";
  if (key === MODIFIED_BY_FILTER_KEY) return "Last modified by";
  return dimensions.find((d) => String(d.id) === key)?.name ?? `Dimension ${key}`;
}

function valueLabel(value: string): string {
  if (value === EMPTY_VALUE_TOKEN) return "(not set)";
  if (value === HAS_VALUE_TOKEN) return "(any value)";
  return value;
}

function describeFilters(search: string, filters: TActiveFilters, dimensions: TOrganizationProjectDimension[]): string {
  const clauses: string[] = [];
  if (search.trim()) clauses.push(`name contains "${search.trim()}"`);
  for (const [key, values] of Object.entries(filters)) {
    if (values.size === 0) continue;
    clauses.push(`${filterLabel(key, dimensions)} is ${[...values].map(valueLabel).join(" or ")}`);
  }
  return clauses.join("; ");
}

export function portfolioFiltersAreActive(search: string, filters: TActiveFilters): boolean {
  return search.trim().length > 0 || Object.values(filters).some((values) => values.size > 0);
}

export function buildPortfolioChatScope(
  visibleProjects: TProject[],
  search: string,
  filters: TActiveFilters,
  dimensions: TOrganizationProjectDimension[]
): TPortfolioChatScope | null {
  if (!portfolioFiltersAreActive(search, filters)) return null;

  const projectIds: number[] = [];
  const draftIds: number[] = [];

  for (const project of visibleProjects) {
    if (project.id.startsWith(DRAFT_ID_PREFIX)) {
      const draftId = Number(project.id.slice(DRAFT_ID_PREFIX.length));
      if (Number.isFinite(draftId)) draftIds.push(draftId);
      continue;
    }
    const rowIds = project.versions?.length ? project.versions.map((v) => v.id) : [project.id];
    for (const id of rowIds) {
      const numericId = Number(id);
      if (Number.isFinite(numericId)) projectIds.push(numericId);
    }
  }

  return { projectIds, draftIds, description: describeFilters(search, filters, dimensions) };
}
